import Game from '../../Game';
import { loadAudio } from '../Audio/AudioHelper';
import IBaseWorldData from './Data/IBaseWorldData';
import SagaMapHandler from './Handler/Map/SagaMapHandler';
import { isShopSupported } from '../../Utility/Platform';
import { WorldDataHelper } from '../../Utility/WorldDataHelper';
import { MathHelper } from '../../Utility/MathHelper';
import { AUDIO_PACKS } from '../Audio';
import { ResourceDriver } from '../../Utility/Resource/ResourceManager';
import { LoadEvents, LoadEventType, LoadtimeTrackerSingleton } from '../../Utility/LoadtimeTracker';
import { addDragonbonesResources } from '../../Utility/Resource/ResourceHelper';
import { DRAGONBONES_PATH } from '../../Constants/Dragonbones';
import { QuestStateHelper } from '../../State/Player/Quests/QuestStateHelper';
import { IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { ThemeSettings } from 'Constants/Theme';

/**
 * Set to true to force-load everything that loads conditionally (for debugging)
 */
const PRELOAD_DEBUG: boolean = false;

export async function preload(worldNumber: number) {
    LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.SOFA_worldSystemPreload, LoadEventType.Start);
    const worldDataKey = `worldData${worldNumber}`;
    Game.resource.add(worldDataKey, `resource/data/world/world_${worldNumber}.json`);
    Game.resource.add('atlas-star-chest', 'resource/graphics/sprite_sheets/starchests.json');
    Game.resource.add('atlas-quests', 'resource/graphics/sprite_sheets/quests.json');

    Game.resource.add(
        'lucky_wheel',
        'resource/graphics/sprite_sheets/lucky_wheel.json'
    );

    addDragonbonesResources('prelvl_booster_icon_check', `${DRAGONBONES_PATH}/PreBoosterCheck/`);
    addDragonbonesResources('prelvl_booster_icon_sparkles', `${DRAGONBONES_PATH}/PreBoosterSparkles/`);

    const state = Game.store.getState();
    const shopSupported = isShopSupported(state);

    if (shopSupported || PRELOAD_DEBUG) {
        IAPStateHelper.preloadOffer(state);

        if (state.playerState.purchases === 0 || PRELOAD_DEBUG) {
            addDragonbonesResources('starter_pack_offer_popup', `${DRAGONBONES_PATH}/StarterPack/`);

            Game.resource.add(
                'firework2_exp2_skeleton_data',
                'resource/dragonbones/ThankYouFirstPurchase/firework2_exp2_ske.json'
            );
            Game.resource.add(
                'firework2_exp2_texture_data',
                'resource/dragonbones/ThankYouFirstPurchase/firework2_exp2_tex.json'
            );
            Game.resource.add(
                'firework2_exp2_texture',
                'resource/dragonbones/ThankYouFirstPurchase/firework2_exp2_tex.png'
            );
        }
    }

    ThemeSettings.WORLD_SYSTEM_PRELOAD_ASSETS.forEach(asset => {
        Game.resource.add(asset.key, asset.path);
    });

    await Game.resource.wait(worldDataKey);
    const worldData = Game.resource.get<IBaseWorldData>(worldDataKey);

    SagaMapHandler.loadTiles(worldNumber);
    const worldAudioClip = loadAudio(worldData.audio);

    // Load star chest animations if star chest level exists and is on the current world
    const worldLimits = WorldDataHelper.getWorldLevelLimits(state.worldState.worldNumber);
    if (
        (
            state.playerState.starChestLevel &&
            MathHelper.isWithinRange(state.playerState.starChestLevel, worldLimits.min, worldLimits.max)
        ) || PRELOAD_DEBUG
    ) {
        preloadStarChestAnimation();
    }

    if (QuestStateHelper.isQuestsFeatureUnlocked(state.playerState) || PRELOAD_DEBUG) {
        preloadQuestsAnimation();
    }

    preloadDailyLoginAnimation();

    preloadCropUnlockAnimation();

    preloadFieldUnlockAnimation();

    Game.resource.add(AUDIO_PACKS.petSystem, undefined, ResourceDriver.SECONDARY);
    Game.resource.add(AUDIO_PACKS.wheelSystem, undefined, ResourceDriver.SECONDARY);

    await Game.resource.load();

    LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.SOFA_worldSystemPreload, LoadEventType.End);

    return {
        worldData,
        worldAudioClip,
    };
}

function preloadStarChestAnimation() {
    Game.resource.add(`chests_all_skeleton_data`, `resource/dragonbones/star_chests/chests_all_ske.json`);
    Game.resource.add(`chests_all_texture`, `resource/dragonbones/star_chests/chests_all_tex.png`);
    Game.resource.add(`chests_all_texture_data`, `resource/dragonbones/star_chests/chests_all_tex.json`);
}

function preloadQuestsAnimation() {
    Game.resource.add(
        `quest_complete_animations_skeleton_data`,
        `resource/dragonbones/Quests/quest_complete_animations_ske.json`
    );
    Game.resource.add(
        `quest_complete_animations_texture`,
        `resource/dragonbones/Quests/quest_complete_animations_tex.png`
    );
    Game.resource.add(
        `quest_complete_animations_texture_data`,
        `resource/dragonbones/Quests/quest_complete_animations_tex.json`
    );
}

function preloadDailyLoginAnimation() {
    Game.resource.add(
        `daily_login_animations_skeleton_data`,
        `resource/dragonbones/DailyLogin/daily_login_animations_ske.json`
    );
    Game.resource.add(
        `daily_login_animations_texture`,
        `resource/dragonbones/DailyLogin/daily_login_animations_tex.png`
    );
    Game.resource.add(
        `daily_login_animations_texture_data`,
        `resource/dragonbones/DailyLogin/daily_login_animations_tex.json`
    );
}

function preloadCropUnlockAnimation() {
    Game.resource.add(
        `unlock_field_popup_skeleton_data`,
        `resource/dragonbones/CropUnlock/unlock_field_popup_ske.json`
    );
    Game.resource.add(`unlock_field_popup_texture`, `resource/dragonbones/CropUnlock/unlock_field_popup_tex.png`);
    Game.resource.add(`unlock_field_popup_texture_data`, `resource/dragonbones/CropUnlock/unlock_field_popup_tex.json`);
}

function preloadFieldUnlockAnimation() {
    Game.resource.add(`unlock_share_skeleton_data`, `resource/dragonbones/ShareUnlock/unlock_share_ske.json`);
    Game.resource.add(`unlock_share_texture`, `resource/dragonbones/ShareUnlock/unlock_share_tex.png`);
    Game.resource.add(`unlock_share_texture_data`, `resource/dragonbones/ShareUnlock/unlock_share_tex.json`);
}
